// src/view/RegistrarBitacoras.jsx
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

/* =================== Helpers UI =================== */
const EmptyState = ({
  title = "Sin registros",
  subtitle = "Aún no has agregado actividades.",
}) => (
  <div className="text-center text-muted py-4">
    <div className="display-6 mb-2">🗒️</div>
    <div className="fw-semibold">{title}</div>
    <div className="small">{subtitle}</div>
  </div>
);

const Pill = ({ children, variant = "secondary" }) => (
  <span className={`badge rounded-pill text-bg-${variant}`}>{children}</span>
);

const safe = (v, fallback = "—") => (v ?? v === 0 ? v : fallback);

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return "—";
  }
};

const fullName = (p1, p2, a1, a2) =>
  [p1, p2, a1, a2].filter(Boolean).join(" ").trim() || "—";

const toYMD = (d) => {
  if (!d) return "";
  try {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const da = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${da}`;
  } catch {
    return "";
  }
};

/* =================== Modal: Tabla de Detalles =================== */
const DetalleTableModal = ({
  show,
  onClose,
  actividades,
  onDelete,
  bitacoraIndex,
}) => {
  if (!show) return null;

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block" }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content shadow-lg border-0">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title mb-1">
                  Detalles —{" "}
                  <span className="text-success">Bitácora {bitacoraIndex + 1}</span>
                </h5>
                <div className="small text-muted">
                  <i className="fa-regular fa-rectangle-list me-1"></i>
                  {actividades.length} actividad{actividades.length !== 1 ? "es" : ""} registrada
                  {actividades.length !== 1 ? "s" : ""}
                </div>
              </div>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body pt-3">
              {actividades.length === 0 ? (
                <EmptyState
                  title="Sin actividades"
                  subtitle="Usa 'Añadir actividad' para crearlas."
                />
              ) : (
                <div className="table-responsive rounded-3 border">
                  <table className="table table-sm table-striped align-middle mb-0">
                    <thead className="table-light">
                      <tr className="text-muted">
                        <th style={{ minWidth: 240 }}>Descripción</th>
                        <th style={{ width: 120 }}>Inicio</th>
                        <th style={{ width: 120 }}>Fin</th>
                        <th style={{ minWidth: 200 }}>Evidencia</th>
                        <th style={{ minWidth: 220 }}>Observaciones</th>
                        <th className="text-end" style={{ width: 90 }}>
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {actividades.map((a, i) => (
                        <tr key={`act-${i}`}>
                          <td className="fw-semibold">
                            {a.descripcion || (
                              <em className="text-muted">Sin descripción</em>
                            )}
                          </td>
                          <td>
                            <Pill variant="light">{a.inicio || "—"}</Pill>
                          </td>
                          <td>
                            <Pill variant="light">{a.fin || "—"}</Pill>
                          </td>
                          <td className="text-truncate" style={{ maxWidth: 280 }}>
                            {a.evidencia || <span className="text-muted">—</span>}
                          </td>
                          <td className="text-truncate" style={{ maxWidth: 280 }}>
                            {a.observaciones || <span className="text-muted">—</span>}
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => onDelete(i)}
                              title="Eliminar"
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan={6} className="small text-muted py-2 px-3">
                          <i className="fa-regular fa-circle-question me-1"></i>
                          Consejo: agrega evidencia (link/archivo/entregable) para soporte.
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer border-0">
              <button className="btn btn-secondary" onClick={onClose}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show"></div>
    </>
  );
};

/* =================== Modal: Formulario de Actividad =================== */
const ActividadFormModal = ({ show, onClose, onSave }) => {
  const initial = useMemo(
    () => ({
      descripcion: "",
      inicio: "",
      fin: "",
      evidencia: "",
      observaciones: "",
    }),
    []
  );

  const [form, setForm] = useState(initial);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (show) {
      setForm(initial);
      setTouched(false);
    }
  }, [show, initial]);

  if (!show) return null;

  const isValid = () => !!form.descripcion?.trim() && !!form.inicio && !!form.fin;

  const guardar = () => {
    setTouched(true);
    if (!isValid()) return;

    onSave(form);   // guarda en el estado local de la bitácora
    onClose();      // cierra el modal correctamente
  };

  const invalid = (field) => touched && !String(form[field] || "").trim();

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block" }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content shadow-lg border-0">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title mb-1">Añadir actividad</h5>
                <div className="small text-muted">
                  Completa campos requeridos <Pill variant="danger">(*)</Pill>
                </div>
              </div>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body pt-3">
              <div className="mb-3">
                <label className="form-label small fw-semibold">
                  Descripción <span className="text-danger">*</span>
                </label>
                <textarea
                  className={`form-control ${invalid("descripcion") ? "is-invalid" : ""}`}
                  rows={3}
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  placeholder="Describe la actividad realizada…"
                />
                {invalid("descripcion") && (
                  <div className="invalid-feedback">La descripción es obligatoria.</div>
                )}
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">
                    Fecha de Inicio <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${invalid("inicio") ? "is-invalid" : ""}`}
                    value={form.inicio}
                    onChange={(e) => setForm({ ...form, inicio: e.target.value })}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-semibold">
                    Fecha de Fin <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${invalid("fin") ? "is-invalid" : ""}`}
                    value={form.fin}
                    onChange={(e) => setForm({ ...form, fin: e.target.value })}
                  />
                </div>
              </div>

              <div className="mt-3">
                <label className="form-label small fw-semibold">Evidencia</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.evidencia}
                  onChange={(e) => setForm({ ...form, evidencia: e.target.value })}
                  placeholder="Documento, proceso, entregable, enlace, etc…"
                />
              </div>

              <div className="mt-3">
                <label className="form-label small fw-semibold">Observaciones</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.observaciones}
                  onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                  placeholder="Comentarios, dificultades, notas…"
                />
              </div>
            </div>

            <div className="modal-footer border-0">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Cerrar
              </button>

              <button type="button" className="btn btn-success" onClick={guardar}>
                <i className="fa-solid fa-check me-1"></i> Guardar actividad
              </button>
            </div>

          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show"></div>
    </>
  );
};

/* =================== Página principal =================== */
const RegistrarBitacoras = () => {
  const navigate = useNavigate();
  const { etapaId, matriculaId, aprendizId } = useParams();
  const location = useLocation();

  // Modo masivo si entra por esa ruta
  const isMassive = location.pathname.startsWith(
    "/etapaproductiva/bitacoras/registromasivo/"
  );

  const formRef = useRef(null);

  // 12 bitácoras (cada una: array de actividades en estado local)
  const [bitacoras, setBitacoras] = useState(Array.from({ length: 12 }, () => []));
  const [bitacoraIds, setBitacoraIds] = useState(Array.from({ length: 12 }, () => null));

  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ etapa: null, aprendiz: null, matricula: null });

  // toolbar
  const [query, setQuery] = useState("");
  const [onlyWithData, setOnlyWithData] = useState(false);

  // modales
  const [showTable, setShowTable] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [activeIdx, setActiveIdx] = useState(null);

  // excel
  const [downloading, setDownloading] = useState(false);

  // ====== Bulk (masivo) ======
  const [bulkForm, setBulkForm] = useState({
    etapaPracticaObjectId: "",
    numeroDesde: 1,
    numeroHasta: 12,
    estadoBitacora: "BORRADOR",
    soloActivos: true,
    skipWhenNoEtapa: true,
    mergeMode: "replace", // en masivo casi siempre conviene replace
  });
  const [bulkLoading, setBulkLoading] = useState(false);
  const bulkSet = (k, v) => setBulkForm((prev) => ({ ...prev, [k]: v }));

  const tituloPagina = isMassive
    ? "Registro Masivo de Bitácoras"
    : "Bitácoras de Etapa Productiva";

  // atajos meta
  const grupo = meta?.matricula?.grupoObjectId;
  const programa = grupo?.programaObjectId;

  const nombreEnte =
    meta?.etapa?.nombreEnteconformador ??
    meta?.matricula?.enteObjectId?.nombreEnteconformador ??
    "—";

  const instructor = meta?.etapa?.instructorObjectId || null;
  const jefe = meta?.etapa?.jefeObjectId || null;

  const instructorNombre = instructor
    ? fullName(
      instructor?.nombre1Instructor,
      instructor?.nombre2Instructor,
      instructor?.apellido1Instructor,
      instructor?.apellido2Instructor
    )
    : "—";

  const jefeNombre = jefe
    ? fullName(jefe?.nombre1Jefe, jefe?.nombre2Jefe, jefe?.apellido1Jefe, jefe?.apellido2Jefe)
    : "—";

  const etapaFechaInicio = meta?.etapa?.fechaInicio || meta?.etapa?.fechaInicioEtapaPractica;
  const etapaFechaFin = meta?.etapa?.fechaFin || meta?.etapa?.fechaFinEtapaPractica;

  const nombreAprendiz = () => {
    const a = meta?.aprendiz;
    if (!a) return "—";
    return (
      fullName(
        a?.nombre1Aprendiz,
        a?.nombre2Aprendiz,
        a?.apellido1Aprendiz,
        a?.apellido2Aprendiz
      ) || "—"
    );
  };

  /* =================== Mappers =================== */
  const mapActividadToPayload = (a) => ({
    descripcionActividad: a.descripcion || "",
    fechaInicioActividad: a.inicio || "",
    fechaFinActividad: a.fin || "",
    evidenciaActividad: a.evidencia || "",
    observacionesActividad: a.observaciones || "",
  });

  const buildActivitiesByNumeroFromState = () => {
    const byNumero = {};
    bitacoras.forEach((rows, idx) => {
      if (Array.isArray(rows) && rows.length > 0) {
        byNumero[String(idx + 1)] = rows.map(mapActividadToPayload);
      }
    });
    return byNumero;
  };

  const dedupeActs = (arr) => {
    const seen = new Set();
    return (arr || []).filter((a) => {
      const key = [
        (a.descripcionActividad || "").trim().toLowerCase(),
        a.fechaInicioActividad || "",
        a.fechaFinActividad || "",
        (a.evidenciaActividad || "").trim().toLowerCase(),
        (a.observacionesActividad || "").trim().toLowerCase(),
      ].join("|");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  /* =================== GET principal =================== */
  const getData = useCallback(async () => {
    // En modo masivo NO necesitamos etapaId/aprendizId para dibujar la plantilla local
    if (isMassive) {
      setLoading(false);
      return;
    }

    if (!etapaId || !aprendizId || !matriculaId) {
      setLoading(false);
      Swal.fire({
        icon: "warning",
        title: "Faltan parámetros",
        text: "Verifica que la ruta tenga etapaId, aprendizId y matriculaId.",
      });
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.get(
        `/api/bitacoras/listbyrelacion/${etapaId}/${aprendizId}/${matriculaId}`
      );

      const next = Array.from({ length: 12 }, () => []);
      const nextIds = Array.from({ length: 12 }, () => null);

      (data?.bitacoras || []).forEach((b) => {
        const idx = Number(b?.numeroBitacora) - 1;
        if (idx >= 0 && idx < 12) {
          const acts = Array.isArray(b?.actividadesBitacora)
            ? b.actividadesBitacora.map((a) => ({
              descripcion: a?.descripcionActividad || "",
              inicio: toYMD(a?.fechaInicioActividad),
              fin: toYMD(a?.fechaFinActividad),
              evidencia: a?.evidenciaActividad || "",
              observaciones: a?.observacionesActividad || "",
            }))
            : [];
          next[idx] = acts;
          nextIds[idx] = b?._id || null;
        }
      });

      setBitacoras(next);
      setBitacoraIds(nextIds);

      setMeta({
        etapa: data?.meta?.etapa || null,
        aprendiz: data?.meta?.aprendiz || null,
        matricula: data?.meta?.matricula || null,
      });
    } catch (err) {
      console.error("Error cargando bitácoras:", err?.message);
      setBitacoras(Array.from({ length: 12 }, () => []));
      setBitacoraIds(Array.from({ length: 12 }, () => null));
      setMeta({ etapa: null, aprendiz: null, matricula: null });

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar las bitácoras.",
      });
    } finally {
      setLoading(false);
    }
  }, [isMassive, etapaId, aprendizId, matriculaId]);

  useEffect(() => {
    getData();
  }, [getData]);

  /* =================== Actividades UI local =================== */
  const addActividad = (actividad) => {
    if (activeIdx === null) return;
    setBitacoras((prev) => {
      const copy = [...prev];
      copy[activeIdx] = [...copy[activeIdx], actividad];
      return copy;
    });
  };

  const clearBitacora = (idxB) => {
    setBitacoras((prev) => {
      const copy = [...prev];
      copy[idxB] = [];
      return copy;
    });
  };

  /* =================== Persistencia individual =================== */
  const saveBitacora = async (numeroBitacora) => {
    if (isMassive) return;

    if (!etapaId || !aprendizId || !matriculaId) {
      Swal.fire({
        icon: "warning",
        title: "Faltan identificadores",
        text: "Verifica etapaId, aprendizId y matriculaId.",
      });
      return;
    }

    const rows = bitacoras[numeroBitacora - 1] || [];
    if (!rows.length) {
      Swal.fire({
        icon: "info",
        title: `Bitácora ${numeroBitacora}`,
        text: "No tiene actividades para guardar.",
      });
      return;
    }

    try {
      const payload = {
        etapaPracticaObjectId: etapaId,
        aprendizObjectId: aprendizId,
        matriculaObjectId: matriculaId,
        numeroBitacora: Number(numeroBitacora),
        actividadesBitacora: rows.map(mapActividadToPayload),
      };

      await axios.post("/api/bitacoras/add", payload);

      await getData();
      Swal.fire({
        icon: "success",
        title: `Bitácora ${numeroBitacora} creada`,
        showConfirmButton: false,
        timer: 1400,
      });
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "No se pudo guardar.", "error");
    }
  };

  const updateBitacora = async (numeroBitacora) => {
    if (isMassive) return;

    const bitId = bitacoraIds[numeroBitacora - 1];
    if (!bitId) {
      Swal.fire({
        icon: "warning",
        title: "No existe en BD",
        text: `La Bitácora ${numeroBitacora} no tiene _id para actualizar.`,
      });
      return;
    }

    const rows = bitacoras[numeroBitacora - 1] || [];
    if (!rows.length) {
      Swal.fire({
        icon: "info",
        title: `Bitácora ${numeroBitacora}`,
        text: "No tiene actividades para actualizar.",
      });
      return;
    }

    try {
      const payload = { actividadesBitacora: rows.map(mapActividadToPayload) };
      await axios.put(`/api/bitacoras/update/${bitId}`, payload);

      await getData();
      Swal.fire({
        icon: "success",
        title: `Bitácora ${numeroBitacora} actualizada`,
        showConfirmButton: false,
        timer: 1400,
      });
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "No se pudo actualizar.", "error");
    }
  };

  /* =================== Excel =================== */
  const descargarExcelBitacora = async (numeroBitacora) => {
    try {
      if (!Number.isInteger(numeroBitacora) || numeroBitacora < 1 || numeroBitacora > 12) {
        Swal.fire("Atención", "Número de bitácora inválido.", "info");
        return;
      }

      const bitId = bitacoraIds[numeroBitacora - 1];
      if (!bitId) {
        Swal.fire({
          icon: "info",
          title: `Bitácora ${numeroBitacora} aún no existe`,
          text: "Guárdala primero para poder generar el Excel.",
        });
        return;
      }

      if (downloading) return;
      setDownloading(true);

      const resp = await axios.get(`/api/bitacora/generar-excel/${bitId}`, {
        responseType: "blob",
      });

      const cd = resp.headers?.["content-disposition"] || "";
      const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
      const docAprendiz = meta?.aprendiz?.documentoAprendiz || "SIN_DOC";
      const fallbackName = `Bitacora_${numeroBitacora}_${docAprendiz}.xlsx`;
      const fileName = match ? decodeURIComponent(match[1]) : fallbackName;

      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: "success",
        title: `Excel Bitácora ${numeroBitacora} generado`,
        showConfirmButton: false,
        timer: 1400,
      });
    } catch (error) {
      console.error("Error al generar Excel:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "No se pudo generar el Excel.",
        "error"
      );
    } finally {
      setDownloading(false);
    }
  };

  /* =================== Descargar todos los excel =================== */

  const descargarTodosLosExcel = async () => {
    try {
      if (downloading) return;

      // Bitácoras existentes (con _id en BD)
      const existentes = bitacoraIds
        .map((id, idx) => ({ id, numero: idx + 1 }))
        .filter((x) => !!x.id);

      if (existentes.length === 0) {
        Swal.fire({
          icon: "info",
          title: "No hay bitácoras guardadas",
          text: "Primero guarda las bitácoras para poder generar los Excel.",
        });
        return;
      }

      const confirmar = await Swal.fire({
        icon: "question",
        title: "Descargar todos los Excel",
        html: `<div class="text-start">
              Se descargarán <b>${existentes.length}</b> archivo(s) Excel (solo los que existen en BD).<br/>
              ¿Deseas continuar?
            </div>`,
        showCancelButton: true,
        confirmButtonText: "Sí, descargar",
        cancelButtonText: "Cancelar",
      });

      if (!confirmar.isConfirmed) return;

      setDownloading(true);

      // Descarga secuencial (más estable que paralelo)
      for (const b of existentes) {
        await descargarExcelBitacora(b.numero);

        // pequeña pausa para no saturar el navegador/servidor
        await new Promise((r) => setTimeout(r, 350));
      }

      Swal.fire({
        icon: "success",
        title: "Descarga completa",
        text: "Se descargaron los Excel disponibles.",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudo descargar todos los Excel.", "error");
    } finally {
      setDownloading(false);
    }
  };


  /* =================== Bulk (Masivo) =================== */
  const bulkValidate = () => {
    if (!matriculaId) {
      Swal.fire("Atención", "No se recibió el parámetro matriculaId.", "warning");
      return false;
    }
    const d = Number(bulkForm.numeroDesde) || 1;
    const h = Number(bulkForm.numeroHasta) || 12;
    if (d < 1 || d > 12 || h < 1 || h > 12 || d > h) {
      Swal.fire("Atención", "El rango debe estar entre 1 y 12 y 'desde' ≤ 'hasta'.", "info");
      return false;
    }
    return true;
  };

  const callBulk = async () => {
    if (!bulkValidate()) return;

    setBulkLoading(true);
    try {
      const activitiesByNumeroRaw = buildActivitiesByNumeroFromState();

      const activitiesByNumero = {};
      Object.keys(activitiesByNumeroRaw || {}).forEach((k) => {
        activitiesByNumero[k] = dedupeActs(activitiesByNumeroRaw[k]);
      });

      const body = {
        etapaPracticaObjectId: bulkForm.etapaPracticaObjectId || undefined,
        numeroDesde: Number(bulkForm.numeroDesde),
        numeroHasta: Number(bulkForm.numeroHasta),
        estadoBitacora: bulkForm.estadoBitacora,
        soloActivos: !!bulkForm.soloActivos,
        skipWhenNoEtapa: !!bulkForm.skipWhenNoEtapa,
        dryRun: false,
        mergeMode: bulkForm.mergeMode || "replace",
        activitiesDefault: [], // no inyectar default en este flujo
        activitiesByNumero,
      };

      const { data } = await axios.post(`/api/bitacoras/bulk/${matriculaId}`, body);

      Swal.fire({
        icon: "success",
        title: "Masivo completado",
        html: `
          <div class="text-start">
            <div><b>${data?.message || "Bitácoras procesadas correctamente."}</b></div>
            <div class="mt-2 small text-muted">
              Revisa el resumen en tu backend si lo estás retornando.
            </div>
          </div>
        `,
      });
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || "Error en el masivo.";
      Swal.fire("Error", msg, "error");
    } finally {
      setBulkLoading(false);
    }
  };

  /* =================== Filtro tarjetas =================== */
  const filtered = useMemo(() => {
    return bitacoras
      .map((rows, i) => ({ rows, index: i }))
      .filter(({ rows, index }) => {
        const matches = `bitacora ${index + 1}`.toLowerCase().includes(query.toLowerCase());
        const passData = onlyWithData ? rows.length > 0 : true;
        return matches && passData;
      });
  }, [bitacoras, query, onlyWithData]);

  /* =================== Render =================== */
  return (
    <form ref={formRef} className="container-fluid py-4 bg-light">
      {/* Encabezado contextual con meta */}
      {!loading && !isMassive && (
        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-user-graduate"></i>
                  </div>
                  <div className="w-100">
                    <div className="fw-semibold">{nombreAprendiz()}</div>
                    <div className="small text-muted mt-1">
                      Documento: {safe(meta?.aprendiz?.documentoAprendiz)} · Tel:{" "}
                      {safe(meta?.aprendiz?.telefono1Aprendiz)} · Email:{" "}
                      {safe(meta?.aprendiz?.emailAprendiz)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-regular fa-calendar-check"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Fecha Etapa Productiva</div>
                    <div className="fw-semibold">
                      {fmtDate(etapaFechaInicio)} — {fmtDate(etapaFechaFin)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-info-subtle text-info d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-book"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Programa</div>
                    <div className="fw-semibold">{safe(programa?.nombrePrograma)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-warning-subtle text-warning d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-people-group"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Grupo</div>
                    <div className="fw-semibold">
                      {safe(grupo?.codigoGrupo, meta?.matricula?.fichaGrupo ?? "—")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-secondary-subtle text-secondary d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-building"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Ente Conformador</div>
                    <div className="fw-semibold">{safe(nombreEnte)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-chalkboard-user"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Instructor de Seguimiento</div>
                    <div className="fw-semibold">{safe(instructorNombre)}</div>
                    <div className="small text-muted mt-1">
                      Doc: {safe(instructor?.documentoInstructor)} · Tel:{" "}
                      {safe(instructor?.telefono1Instructor ?? instructor?.telefonoInstructor)} ·
                      Email: {safe(instructor?.emailInstructor)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-dark-subtle text-dark d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-user-tie"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Jefe Inmediato</div>
                    <div className="fw-semibold">{safe(jefeNombre)}</div>
                    <div className="small text-muted mt-1">
                      Doc: {safe(jefe?.documentoJefe)} · Tel:{" "}
                      {safe(jefe?.telefono1Jefe ?? meta?.etapa?.enteObjectId?.jefeTelefono)} ·
                      Email: {safe(jefe?.emailJefe ?? meta?.etapa?.enteObjectId?.jefeCorreo)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header principal + toolbar */}
      <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mb-4">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <h3 className="fw-bold mb-1">{tituloPagina}</h3>
            <div className="text-muted small">
              {isMassive
                ? "Crea/actualiza bitácoras de forma masiva usando tu plantilla local."
                : "Agrega actividades, guarda o actualiza cada bitácora."}
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate(-1)}
              disabled={loading || bulkLoading}
            >
              <i className="fa-solid fa-arrow-rotate-left me-2"></i>
              Volver
            </button>

            {!isMassive && (
              <button
                type="button"
                className="btn btn-outline-dark"
                onClick={getData}
                disabled={loading}
              >
                <i className="fa-solid fa-rotate me-2"></i>
                Recargar
              </button>
            )}

            {!isMassive && (
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={descargarTodosLosExcel}
                disabled={downloading || loading}
                title="Descargar todos los Excel de las bitácoras existentes"
              >
                <i className={`fa-solid ${downloading ? "fa-spinner fa-spin" : "fa-file-excel"} me-1`} />
                Descargar todos los Excel
              </button>
            )}

          </div>
        </div>

        <hr className="my-3" />

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div className="input-group" style={{ maxWidth: 360 }}>
            <span className="input-group-text bg-white">
              <i className="fa-solid fa-magnifying-glass"></i>
            </span>
            <input
              type="search"
              className="form-control"
              placeholder="Buscar por número de bitácora…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="d-flex align-items-center gap-3">
            <div className="form-check">
              <input
                id="chk-data"
                className="form-check-input"
                type="checkbox"
                checked={onlyWithData}
                onChange={(e) => setOnlyWithData(e.target.checked)}
              />
              <label htmlFor="chk-data" className="form-check-label small">
                Sólo con actividades
              </label>
            </div>

            {(loading || bulkLoading) && (
              <div className="small text-secondary">
                <span className="spinner-border spinner-border-sm me-2" />
                Procesando…
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Panel MASIVO */}
      {isMassive && (
        <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mb-4">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h6 className="fw-semibold mb-1">Opciones de Registro Masivo</h6>
              <small className="text-muted">
                Se tomará tu plantilla local (actividades por número) y se enviará a BD para la matrícula.
              </small>
            </div>

            <button
              type="button"
              className="btn btn-primary"
              disabled={bulkLoading}
              onClick={callBulk}
              title="Guardar masivo usando la plantilla local"
            >
              <i className={`fa-solid ${bulkLoading ? "fa-spinner fa-spin" : "fa-cloud-arrow-up"} me-2`} />
              Guardar Masivo
            </button>
          </div>

          <div className="row g-3 mt-2">
            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Número desde</label>
              <input
                type="number"
                className="form-control"
                min={1}
                max={12}
                value={bulkForm.numeroDesde}
                onChange={(e) => bulkSet("numeroDesde", e.target.value)}
              />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Número hasta</label>
              <input
                type="number"
                className="form-control"
                min={1}
                max={12}
                value={bulkForm.numeroHasta}
                onChange={(e) => bulkSet("numeroHasta", e.target.value)}
              />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Estado</label>
              <select
                className="form-select"
                value={bulkForm.estadoBitacora}
                onChange={(e) => bulkSet("estadoBitacora", e.target.value)}
              >
                <option value="BORRADOR">BORRADOR</option>
                <option value="ENVIADA">ENVIADA</option>
                <option value="APROBADA">APROBADA</option>
              </select>
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Etapa (opcional)</label>
              <input
                className="form-control"
                value={bulkForm.etapaPracticaObjectId}
                onChange={(e) => bulkSet("etapaPracticaObjectId", e.target.value)}
                placeholder="ObjectId si quieres forzar"
              />
            </div>

            <div className="col-12">
              <div className="d-flex flex-wrap gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="soloActivos"
                    checked={bulkForm.soloActivos}
                    onChange={(e) => bulkSet("soloActivos", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="soloActivos">
                    Sólo activos
                  </label>
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="skipNoEtapa"
                    checked={bulkForm.skipWhenNoEtapa}
                    onChange={(e) => bulkSet("skipWhenNoEtapa", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="skipNoEtapa">
                    Omitir si no tienen etapa
                  </label>
                </div>
              </div>

              <div className="alert alert-info mt-3 mb-0">
                <i className="fa-solid fa-circle-info me-2"></i>
                Consejo: primero crea tu plantilla local agregando actividades en las bitácoras que quieras,
                luego presiona <b>Guardar Masivo</b>.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grid 12 tarjetas */}
      <div className="row g-4">
        {filtered.map(({ rows, index: idxB }) => {
          const numero = idxB + 1;
          const existe = Boolean(bitacoraIds[idxB]);
          const canPersist = !isMassive; // individual solo en no masivo

          return (
            <div className="col-12 col-md-6 col-lg-4" key={`bitacora-${idxB}`}>
              <div className="card h-100 shadow-sm border-0">
                <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center"
                      style={{ width: 36, height: 36 }}
                    >
                      <i className="fa-regular fa-note-sticky"></i>
                    </div>

                    <div className="d-flex align-items-center">
                      <div className="fw-semibold">Bitácora {numero}</div>
                      {existe && !isMassive && (
                        <span className="badge text-bg-light ms-2">EXISTENTE</span>
                      )}
                    </div>
                  </div>

                  <div className="dropdown">
                    <button
                      className="btn btn-sm btn-outline-secondary dropdown-toggle"
                      data-bs-toggle="dropdown"
                      type="button"
                    >
                      Acciones
                    </button>

                    <ul className="dropdown-menu dropdown-menu-end">
                      <li>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={() => {
                            setActiveIdx(idxB);
                            setShowTable(true);
                          }}
                        >
                          <i className="fa-solid fa-table me-2"></i> Ver detalles (tabla)
                        </button>
                      </li>

                      <li>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={() => {
                            setActiveIdx(idxB);
                            setShowForm(true);
                          }}
                        >
                          <i className="fa-solid fa-plus me-2"></i> Añadir actividad
                        </button>
                      </li>

                      <li>
                        <hr className="dropdown-divider" />
                      </li>

                      <li>
                        <button
                          type="button"
                          className="dropdown-item text-danger"
                          onClick={() => clearBitacora(idxB)}
                        >
                          <i className="fa-solid fa-eraser me-2"></i> Limpiar bitácora
                        </button>
                      </li>

                      {!isMassive && (
                        <li>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => descargarExcelBitacora(numero)}
                            disabled={downloading}
                          >
                            <i className="fa-solid fa-file-excel me-2"></i> Exportar a Excel
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>

                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="small text-muted">Los detalles se ven en “Detalles”.</div>
                    <Pill variant={rows.length ? "success" : "secondary"}>
                      {rows.length} registro{rows.length !== 1 ? "s" : ""}
                    </Pill>
                  </div>

                  <div className="progress mt-3" style={{ height: 6 }}>
                    <div
                      className={`progress-bar ${rows.length ? "bg-success" : "bg-secondary"}`}
                      style={{ width: `${Math.min(rows.length * 10, 100)}%` }}
                      role="progressbar"
                      aria-valuenow={rows.length}
                      aria-valuemin="0"
                      aria-valuemax="10"
                    />
                  </div>

                  {canPersist && (
                    <div className="mt-3 small text-muted">
                      {existe ? "Puedes actualizar en BD." : "Aún no existe en BD (Guardar)."}
                    </div>
                  )}
                </div>

                <div className="card-footer bg-white d-flex justify-content-between align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setActiveIdx(idxB);
                      setShowTable(true);
                    }}
                  >
                    <i className="fa-solid fa-table me-1"></i> Detalles
                  </button>

                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={() => {
                        setActiveIdx(idxB);
                        setShowForm(true);
                      }}
                    >
                      <i className="fa-solid fa-plus me-1"></i> Añadir
                    </button>

                    {canPersist && (
                      existe ? (
                        <button
                          type="button"
                          className="btn btn-dark btn-sm"
                          onClick={() => updateBitacora(numero)}
                          title="Actualizar en BD"
                        >
                          <i className="fa-solid fa-floppy-disk me-1"></i> Actualizar
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-dark btn-sm"
                          onClick={() => saveBitacora(numero)}
                          title="Guardar en BD"
                        >
                          <i className="fa-solid fa-floppy-disk me-1"></i> Guardar
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="col-12">
            <EmptyState
              title="Sin coincidencias"
              subtitle="Intenta con otro término o desmarca 'Sólo con actividades'."
            />
          </div>
        )}
      </div>

      {/* Modal Tabla */}
      {showTable && activeIdx !== null && (
        <DetalleTableModal
          show={showTable}
          onClose={() => {
            setShowTable(false);
            setActiveIdx(null);
          }}
          actividades={bitacoras[activeIdx]}
          onDelete={(i) => {
            setBitacoras((prev) => {
              const copy = [...prev];
              const rows = [...copy[activeIdx]];
              rows.splice(i, 1);
              copy[activeIdx] = rows;
              return copy;
            });
          }}
          bitacoraIndex={activeIdx}
        />
      )}

      {/* Modal Form */}
      {showForm && activeIdx !== null && (
        <ActividadFormModal
          show={showForm}
          onClose={() => {
            setShowForm(false);
            setActiveIdx(null);
          }}
          onSave={addActividad}
        />
      )}
    </form>
  );
};

export default RegistrarBitacoras;
